import React, { useEffect, useState } from "react";
import styles from "./Payments.module.scss";
import { downloadFile, formatDateToReadable } from "utils/constants";
import { debounce, getRefundOption, refundOptions } from "../constants";
import { Controller, useForm } from "react-hook-form";
import SelectCustom from "components/common/Select/Select";
import ViewSVG from "components/SVGComponents/ViewSVG";
import DownloadIconSVG from "components/SVGComponents/DownloadIconSVG";
import { combineClasses, serverFiles } from "utils/utils";
import {
  exportPayments,
  getAllPayments,
  handleRefundStatus,
} from "reducer/admin/paymentSlice";
import { useDispatch, useSelector } from "react-redux";
import CustomHeader from "components/common/CustomHeader/CustomHeader";
import Table from "components/common/Table/Table";
import ReportReason from "../SharedComponents/ReportReason/ReportReason";
import ReportModal from "components/UI/ReportModal/ReportModal";
import Tooltip from "components/common/Tooltip/Tooltip";
import FilterModal from "./FilterModal";

const Payment = () => {
  const [pageSelected, setPageSelected] = useState(1);
  const [rowsPerPageValue, setRowsPerPageValue] = useState(10);
  const [reportModal, setReportModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState();
  const [reportText, setReportText] = useState("");
  const [reasonModal, setReasonModal] = useState("");
  const [search, setSearch] = useState("");
  const { control } = useForm();
  const dispatch = useDispatch();

  const { payments, totalCount, isPaymentsLoading, isExporting } = useSelector(
    (state) => state.payments
  );

  const handleToggleStatus = async (row) => {
    try {
      await dispatch(
        handleRefundStatus({
          id: row.id,
          status:
            row?.refundStatus?.toLowerCase() === "pending"
              ? "PROCESSED"
              : "PENDING",
          message: reportText,
        })
      ).unwrap();
      setReportModal(false);
      setReportText("");
    } catch (error) {
      console.log("error", error);
    }
  };

  const handleExport = async () => {
    const res = await dispatch(exportPayments()).unwrap();
    downloadFile(res);
  };

  const handleRefundModal = async (value, row) => {
    if (value === "processed") {
      setReportModal(true);
    } else {
      handleToggleStatus(row);
    }
  };

  const handleSearch = debounce((value) => {
    dispatch(getAllPayments({ pageSelected, search: value }));
    setSearch(value);
  }, 500);

  const columns = [
    {
      name: "Business Name",
      selector: (row) => row?.businessName,
      width: "13%",
    },
    {
      name: "Transaction ID",
      selector: (row) => <Tooltip data={row?.refId} /> || "-",
      width: "22%",
    },
    {
      name: "Date & Time",
      selector: (row) => formatDateToReadable(row.createdAt) || "-",
      width: "15%",
    },
    {
      name: "Status",
      selector: (row) => {
        return (
          <p className={styles.status}>{row?.paymentStatus?.toLowerCase()}</p>
        );
      },
      width: "10%",
    },
    {
      name: "Amount",
      selector: (row) => row.amount || "-",
      width: "10%",
    },
    {
      name: "Refund",
      selector: (row) => (
        <Controller
          name={`refund-${row.id}`}
          control={control}
          defaultValue={getRefundOption(row?.refundStatus?.toLowerCase())}
          render={({ field }) => (
            <SelectCustom
              {...field}
              onChange={(selectedOption) => {
                field.onChange(selectedOption);
                handleRefundModal(selectedOption?.value, row);
                setSelectedRow(row);
              }}
              value={getRefundOption(row?.refundStatus?.toLowerCase())}
              options={refundOptions}
              styles={{
                control: (provided) => ({
                  ...provided,
                  cursor: "pointer",
                  backgroundColor: "transparent",
                  boxShadow: "none",
                  outline: "none",
                  border: "1px solid #CCCCCC",
                  borderRadius: "8px",
                  minHeight: "auto",
                  height: "auto",
                }),
                singleValue: (provided) => ({
                  ...provided,
                  color: "#000",
                  fontWeight: "600",
                  width: "3.8rem",
                  whiteSpace: "nowrap",
                }),
                menuList: (provided) => ({
                  ...provided,
                  paddingTop: "0rem",
                  paddingBottom: "0rem",
                  borderRadius: "0.1875rem",
                }),
                option: (provided) => ({
                  ...provided,
                  padding: "0.1875rem 0.5rem",
                  fontSize: "0.625rem",
                  cursor: "pointer",
                }),
                dropdownIndicator: (provided) => ({
                  ...provided,
                  color: "#000",
                  padding: "0rem 0.375rem 0rem 0rem",
                  ":hover": {
                    color: "#000",
                  },
                }),
              }}
            />
          )}
        />
      ),
      width: "18%",
    },
    {
      name: "Action",
      selector: (row) => {
        return (
          <div className={styles.actions}>
            <a
              href={serverFiles(row?.receipt)}
              download
              target="_blank"
              rel="noopener noreferrer"
              className={combineClasses(
                styles.actionIcon,
                !row?.receipt && styles.disabled
              )}
            >
              <DownloadIconSVG color="#7E7E7E" />
            </a>

            {row?.message && (
              <span
                className={styles.view}
                onClick={() => setReasonModal(row.message)}
              >
                <ViewSVG />
              </span>
            )}
          </div>
        );
      },
      width: "12%",
    },
  ];

  useEffect(() => {
    dispatch(getAllPayments({ pageSelected }));
  }, [pageSelected, dispatch]);

  const [openModal, setOpenModal] = useState(false);

  return (
    <>
      <div className={styles.main}>
        <CustomHeader
          handleSearch={handleSearch}
          isExporting={isExporting}
          handleExport={handleExport}
          openModal={openModal}
          setOpenModal={setOpenModal}
        />
        {openModal && (
          <FilterModal onClose={() => setOpenModal(false)} search={search} />
        )}

        <Table
          columns={columns}
          data={payments || []}
          className={styles.candidatesTable}
          progressPending={isPaymentsLoading}
          paginationProps={{
            isPagination: true,
            tableName: "Payments",
            pageSelected,
            totalCount: totalCount,
            rowsPerPageValue,
            setRowsPerPageValue,
            setPageSelected,
          }}
        />
        {reportModal && (
          <ReportModal
            reportModal={reportModal}
            setReportModal={setReportModal}
            coverImage={false}
            type="user"
            handleSubmit={() => handleToggleStatus(selectedRow)}
            setReport={setReportText}
          />
        )}
      </div>
      {reasonModal && (
        <ReportReason reason={reasonModal} setReasonModal={setReasonModal} />
      )}
    </>
  );
};

export default Payment;
